/* eslint-disable @typescript-eslint/no-explicit-any */
import type dayjs from "dayjs";
import type { MutableRefObject, PropsWithChildren } from "react";

import type {
  ComputedDataKey,
  DATA_E2E_KEY,
  DATA_E2E_TEMPLATE_KEY,
  TRANSLATION_KEY,
  TRANSLATION_PARAMS_KEY,
} from "../constants";
import type { IPassengerInfo } from "../dto";
import type {
  BulkProduceTypeEnum,
  DirectionEnum,
  EditFormMode,
  LoginAuthType,
  RideTypeCategoryEnum,
  TopCalendarMode,
  UrlQueryMatchTypeEnum,
  WaypointCategoryEnum,
} from "../enums";
import type { DropdownResolverName } from "../helpers";
import type { TTimeHHmm } from "./date-time.models";
import type { TResolveFn } from "./drawers.models.props";
import type { TLatLngExpression } from "./map.models";

export const NO_CACHE_KEY = "NO_CACHE_KEY";
export type RydeUiUnitDropdownCacheKey =
  RydeUiUnit | `${RydeUiUnit}_${string}` | typeof NO_CACHE_KEY;
export type RydeUiUnit = RydeAppUnit | RydeAdminUnit | RydeCustomUnit;

export type TCrudEditAtomKey = [RydeUiUnit, EditFormMode];

export type RydeAppUnit =
  | "addition"
  | "area"
  | "areasGroup"
  | "booking"
  | "businessUnit"
  | "complaint"
  | "course"
  | "dashboard"
  | "dataUpload"
  | "dayGroup"
  | "dayType"
  | "disableHour"
  | "driver"
  | "feedback"
  | "feedbackSolution"
  | "feedbackSubject"
  | "flight"
  | "foodSupplier"
  | "incident"
  | "incidentType"
  | "journey"
  | "meal"
  | "notification"
  | "passenger"
  | "passengerSector"
  | "passengerType"
  | "priceRate"
  | "produceButton"
  | "reason"
  | "reasonCategory"
  | "report"
  | "ride"
  | "rideMap"
  | "rideProduction"
  | "rideType"
  | "shift"
  | "shiftTemplate"
  | "site"
  | "station"
  | "stationDistance"
  | "stationRoute"
  | "supplier"
  | "supplierPriceRate"
  | "systemManager"
  | "systemParam"
  | "systemTag"
  | "tracking"
  | "unit"
  | "unknown"
  | "user"
  | "vehicle"
  | "vehicleType"
  | "view"
  | "geoArea";

export type RydeAdminUnit =
  | "adminAction"
  | "adminTools"
  | "alertConfiguration"
  | "apiKey"
  | "auditLog"
  | "backup"
  | "process"
  | "busStation"
  | "mapCacheStat"
  | "company"
  | "customField"
  | "entity"
  | "etlJobLog"
  | "etlSchedule"
  | "fieldType"
  | "formSchema"
  | "globalConfig"
  | "gridSchema"
  | "language"
  | "messagingLog"
  | "permission"
  | "aiConversation"
  | "providerLog"
  | "report"
  | "role"
  | "supplierQuarantine"
  | "user"
  | "userRole"
  | "webhook"
  | "webhookDelivery"
  | "geoArea";

export type RydeCustomUnit = "system" | "landing";

export const ActionsEnum = {
  Manager: "manage",
  List: "list",
  Read: "read",
  Create: "create",
  Update: "update",
  Delete: "delete",
  Reject: "reject",
  Produce: "produce",
  Execute: "execute",
  ViewPrices: "view_prices",
  UpdatePrices: "update_prices",
  ViewSupplierPrices: "view_supplier_prices",
  UpdateSupplierPrices: "update_supplier_prices",
  ReadSystemSettings: "read_system_settings",
  ViewReports: "view_reports",
  CreateCompanyReports: "create_company_reports",
  CreateUserReports: "create_user_reports",
  CreateGlobalReports: "create_global_reports",
  CreateInPast: "createInPast",
  ViewLogs: "view_logs",
  CreateUserView: "create_user_view",
  CreateCompanyView: "create_company_view",
  CreateGlobalView: "create_global_view",
  EditAtFreezeTime: "edit_at_freeze_time",
  IgnoreFreezeTime: "ignore_freeze_time",
  ActAsAdmin: "act_as_admin",
  ActAsSupplier: "act_as_supplier",
  ActAsOperator: "act_as_operator",
  ApproveConflictWithinProductionLock:
    "approve_conflict_within_production_lock",
  ShowUnsent: "show_unsent",
  Approve: "approve",
  HandleSecrets: "handle_secrets",
  HandleSmsSecrets: "handle_sms_secrets",
  DontSeeRideChangesPopup: "dont_see_ride_changes_popup",
  Cancel: "cancel",
  Publish: "publish",
  CreateReturn: "create_return",
  UpdateStatus: "update_status",
  UpdateDuration: "update_duration",
  RecalculatePrices: "recalculate_prices",
  ModifyRegularDays: "modify_regular_days",
  ViewRideSupplier: "view_ride_supplier",
  EditRideSupplier: "edit_ride_supplier",
  Rebuild: "rebuild",
  SendToSupplier: "send_to_supplier",
  UpdateDriver: "update_driver",
  UpdateVehicle: "update_vehicle",
  CreateRideInPast: "create_in_past",
  UpdateRideInPast: "update_in_past",
  EditNewOnly: "edit_new_only",
  CreateOtherSite: "create_other_site",
  Renew: "renew",
  markAsDone: "mark_as_done",
  markAsDoneApproved: "mark_as_done_approved",
  markAsDoneReceived: "mark_as_received",
  ViewAiChatDetails: "view_ai_chat_details",
  ViewDiagnosticColumns: "view_diagnostic_columns",
  AllowSendBelowMinimum: "allow_send_below_minimum",
  AllowGlobalRolesInCompanyMode: "allow_global_roles_in_company_mode",
  AllowExport: "allow_export",
  AllowImport: "allow_import",
  SendMessage: "send_message",
} as const;

export type TActionsEnumKeys = keyof typeof ActionsEnum;
export type TPermissionAction = (typeof ActionsEnum)[TActionsEnumKeys];

export const fuzzyServerSearch = new Set<RydeUiUnit>([
  "booking",
  "ride",
  "rideMap",
]);

export type TableActionType = "Sorting" | "Grouping" | "Filters";

export type DtoFieldType = string | number | Date | boolean | null | undefined;

export type TDropdownNullableOption = string | number | null;

export interface IDropdownItem {
  value: number | string;
  label: string;
  isActive?: boolean;
  companyId?: number;
  isExclusive?: boolean;
  supplierId?: number;
  // suppliers specific
  isActiveSpecial?: boolean;
  priority?: number;
  // RYDE-7633: the supplier's external provider (e.g. "YANGO") — lets the dashboard gate the ride
  // Send action when a past ride is assigned to a taxi provider.
  sendToProvider?: string;
  // roles specific
  isNotManagerRole?: boolean;
  // area specific
  areaGroupId?: number;
  areaGroupName?: string;
  defaultSupplierId?: number;
  // vehicle type specific
  maxPassengers?: number;
  userId?: number;
  // Mirrors ISelectListItem, so the two item shapes stay assignable to each other.
  technicalName?: string;
}

export interface IDropdownItemsParams {
  dropdownItemsSuffixOverride?: string;
  dropdownItemsResolverName?: DropdownResolverName;
}

export interface ISelectListItem {
  value: number | string | boolean;
  label: string;
  isActive?: boolean;
  companyId?: number;
  /** Code name behind a translated label; dropdowns show it on hover and match it on search. */
  technicalName?: string;
}

export interface ISelectListNumberItem {
  value: number;
  label: string;
}

export interface IDropdownItemExt extends IDropdownItem {
  [key: string]: DtoFieldType;
}

export interface ISelectListItemExt extends ISelectListItem {
  // for dto fields that can then be used for filtering based on other properties
  [key: string]: DtoFieldType;
}

export interface IDropdownItemRideTypes extends IDropdownItem {
  isRideWithEmptyStationsAllowed?: boolean;
  isRideWithDuplicatedStationsAllowed?: boolean;
  skipSettingSupplierByAreaGroup?: boolean;
  rideTypeCategory?: RideTypeCategoryEnum;
}

export interface IDropdownItemStations extends IDropdownItem {
  id?: number;
  isOfficial?: boolean;
  isDestination?: boolean;
  isDefault?: boolean;
}

export interface IDropdownItemShifts extends IDropdownItem {
  unitIds?: number[] | null;
  // RYDE-8171: booking this shift creates one meal-only (direction X) booking per passenger per date.
  isMealOnly?: boolean;
  direction?: DirectionEnum;
  shiftStartTime?: TTimeHHmm;
  shiftEndTime?: TTimeHHmm;
}

export interface IDropdownItemPassengers extends IDropdownItem {
  unitId?: number | null;
}

export interface IDropdownItemProduceButtons extends IDropdownItem {
  productionAction?: BulkProduceTypeEnum;
}

// Wix-specific interface with location info
export interface IWixDropdownItemStations extends IDropdownItem {
  locationInfo?: {
    fullAddress?: string;
    latitude?: number;
    longitude?: number;
  };
}

export interface ISelectListItemShiftTime extends ISelectListItem {
  shiftStartTime?: TTimeHHmm;
  shiftEndTime?: TTimeHHmm;
}

export interface IRadioGroupItem {
  value: number | string | boolean;
  label: string;
}

export interface ISimpleDtoBase {
  id: number;
}

export interface ISimpleDtoWithNameBase extends ISimpleDtoBase {
  name: string;
}

export interface ISimpleDto extends ISimpleDtoWithNameBase {
  [key: string]: DtoFieldType;
}

export interface ISimplePassengerDto extends ISimpleDtoBase {
  firstName: string;
  lastName: string;
}

export interface ISortableCell extends PropsWithChildren {
  fieldNameResolved: string;
  sortingDisabled?: boolean;
  groupingDisabled?: boolean;
  translationKey?: string;
  isHiddenFromView?: boolean;
}

export interface ICreatableSelectOptions {
  value: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- react-select Creatable metadata key
  __isNew__: boolean;
}

export type TranslationsNS = "general" | "settings";

export type TranslationsResponse = {
  [ns in TranslationsNS]: Record<string, string>;
};

export type RequestBodyModel = Record<string, any> | undefined;
export type RequestHeadersModel = Record<string, any> | undefined;
export interface ILoginModel {
  [LoginAuthType.Username]?: string;
  [LoginAuthType.Phone]?: string;
  [LoginAuthType.Email]?: string;
  email?: string;
  password: string;
  loginType: LoginAuthType.Username | LoginAuthType.Phone | LoginAuthType.Email;
  code?: string;
}
export interface IResetPasswordModel {
  password: string;
  repeatPassword: string;
}

export interface ICoordinate {
  lat: number;
  lng: number;
}

export interface ILatitudeLongitude {
  latitude: number;
  longitude: number;
}

export interface ILocationInfoBase {
  latitude?: number;
  longitude?: number;
  arrivalTime?: TDateISO;
}

export interface ILocationInfoBaseValid extends ILatitudeLongitude {
  arrivalTime?: TDateISO;
}

export interface ILocationInfo extends ILocationInfoBase {
  addressName?: string;
  fullAddress?: string;
  fullPassengersDetails?: IPassengerInfo[];
  stationId?: number;
  passengerList?: string[];
  plannedTime?: TDateISO;
  isDropoff?: boolean;
  /** Routing-only map point — not a passenger station. */
  waypointCategory?: WaypointCategoryEnum;
}

export interface ILocationInfoValid extends ILocationInfoBaseValid {
  fullAddress?: string;
  passengerList?: string[];
  plannedTime?: TDateISO;
  isDropoff?: boolean;
}

export interface IGeoJsonPointGeometry {
  type: "Point";
  coordinates: [number, number];
}
export interface IGeoJsonLineStringGeometry {
  type: "LineString";
  coordinates: [number, number][];
}

export interface IGeoJsonPolygonGeometry {
  type: "Polygon";
  coordinates: [number, number][][];
}

export type TGeoJsonPointGeometry =
  IGeoJsonPointGeometry | IGeoJsonLineStringGeometry | IGeoJsonPolygonGeometry;

export interface IGeoJSONFeature {
  type: "Feature";
  properties: object;
  geometry: TGeoJsonPointGeometry;
}

export interface IGeoJSON {
  type: "FeatureCollection";
  features: IGeoJSONFeature[];
}

/**
 * Rectangle address search is confined to, held by the `addressSearchBounds` system param.
 *
 * Compass edges rather than a corner pair, matching the backend param: the map editor that
 * writes it works in edges, and a corner pair reads as valid when its corners are swapped.
 */
export interface IAddressSearchBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export type PlainObject = Record<string, unknown>;

export type DatesUnit = {
  date1: TDateISO;
  date2: TDateISO;
};

export interface ITopCalendarDropdownRow {
  value: TopCalendarMode;
  label: string;
  datesUnit?: DatesUnit;
}

export interface IPoint {
  x: number;
  y: number;
}

export type DragMotionEvent =
  React.MouseEvent | React.TouchEvent | React.PointerEvent;

export interface ILoginCredentials {
  [LoginAuthType.Username]?: string;
  [LoginAuthType.Phone]?: string;
  [LoginAuthType.Email]?: string;
  password: string;
  recaptchaToken: string;
}

export interface INavigatePossibleActions {
  list: (keepQuery?: boolean) => void;
  listUrl: string;
  new: (keepQuery?: boolean) => void;
  edit: (id: number, keepQuery?: boolean) => void;
  add: (id?: number, keepQuery?: boolean) => void;
}

export type DateDayjsCompatible =
  string | number | Date | dayjs.Dayjs | TDateISO;

export interface IOldSystemMappingInfo {
  sourcePath: string;
  sourceQuery?: string;
  sourceQueryMatchMode?: UrlQueryMatchTypeEnum;
  targetPathAndQuery: string;
  viewOrder?: number;
}

export type TDropdownMode = "single" | "multi" | "tags";

export interface IResolveRejectEditModal {
  editId?: number;
  entity: RydeUiUnit;
  resolveFn?: TResolveFn;
}

export interface IResolveRejectDropdownEditModal {
  editId?: number;
  entity: RydeUiUnit;
  resolveFn?: (value?: number) => void;
}

export type TJsonTypeOption =
  "string" | "number" | "boolean" | "array" | "object" | "date";

export type SearchQueryForOptimize =
  "rides" | "bookings" | "bookingsPassengers" | "reports";

export interface ITranslatableItem {
  [TRANSLATION_KEY]: string;
  [TRANSLATION_PARAMS_KEY]?: TTranslationParams;
  [ComputedDataKey]?: Record<string, any>;
  label?: string;
}

export type TTranslationParams = Record<
  string,
  string | number | ITranslationKeyTranslatedParam
>;

/* allows to use translated not only whole phrase, but also params -
these params are used in the translation function, so they are available in the translation string,
the params object will be like

const params: TTranslationParams = {
  distance: "25km",
  direction: { translationKey: "selectList.A" },
  minutes: 25
};
*/
interface ITranslationKeyTranslatedParam {
  [TRANSLATION_KEY]: string;
}

export interface ILabelItem {
  label: string;
}

export type TInfoItem = string | ITranslatableItem | ILabelItem;

export type TError = string | string[] | Error | ITranslatableItem | ILabelItem;

export interface IFetchOptions {
  abortController?: AbortController;
  backgroundAction?: boolean;
  debugMode?: boolean;
  /** Live-map simple mode: fetch/replace with the latest GPS point only. */
  lastTrackingOnly?: boolean;
  /**
   * Tracking: commit the fetched full trail to the single-ride edit store (editRideTrackingAF) instead
   * of the shared live-map store, so the live-map poll can't trim the opened ride's trail.
   */
  commitToEditStore?: boolean;
}

export interface IStyledAttributes {
  [DATA_E2E_KEY]?: string;
  [DATA_E2E_TEMPLATE_KEY]?: string;
}

export type AbortRef = MutableRefObject<AbortController | null>;

export interface IWarkDistanceAndTime {
  distance: number;
  duration: { minutes: number; seconds: number };
  path: TLatLngExpression[];
}

export type TCompanyId = number;

export type ObjectLiteral<T = any> = {
  [key: string]: T;
};

export interface IBulkUpdateDtoWarnings {
  warnings?: ITranslatableItem[];
}

export interface IStandardApiState<T> {
  isLoading: boolean;
  error?: TError;
  data: T;
}

export interface IObjectLiteral {
  [key: string]: any;
}

/**
 * Interface for DTOs that have title/name properties.
 * Used for type-safe casting when accessing title/name on ValidationClass DTOs in admin edit forms.
 */
export interface IDtoWithTitleOrName {
  title?: string;
  name?: string;
}
